// Xero OAuth callback relay (BUILD_SPEC.md §12; services/api/src/modules/xero/
// connection.ts's header). `infra/lib/api-stack.ts` sets `XERO_REDIRECT_URI` to
// `https://<cloudfront>/settings/xero/callback` — Xero's OAuth redirect lands
// the browser HERE (already authenticated, same app/domain/session), and this
// page's ONLY job is to relay the `code`/`state` (or `error`) off its own URL to
// the already-authenticated `GET /xero/callback`, using the signed-in user's
// existing JWT + X-Tenant-Id exactly like every other API call this app makes —
// never talking to Xero directly itself.
//
// Called via a raw `apiFetch` (not the useApiQuery/useApiMutation hooks): this
// is a one-shot, side-effecting GET (it exchanges a single-use authorization
// code for tokens) that must run exactly once on mount, never retried/refetched
// by TanStack Query's normal query lifecycle — a second exchange of the same
// `code` would fail against Xero regardless of who initiates it.
import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck } from '@tabler/icons-react';
import type { XeroConnectionResponse } from '@zebra/core';
import { apiFetch, ApiRequestError } from '../../../api/client';

export interface XeroCallbackSearch {
  code?: string;
  state?: string;
  error?: string;
  error_description?: string;
}

export function XeroCallbackFlow({ search }: { search: XeroCallbackSearch }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [result, setResult] = useState<XeroConnectionResponse | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    apiFetch<XeroConnectionResponse>('/xero/callback', {
      query: { code: search.code, state: search.state, error: search.error, error_description: search.error_description },
    })
      .then((response) => {
        if (cancelled) return;
        setResult(response);
        setStatus('success');
        void queryClient.invalidateQueries({ queryKey: ['xero', 'connection'] });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMessage(err instanceof ApiRequestError ? err.message : 'Failed to complete the Xero connection.');
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
    // Deliberately runs exactly once on mount (empty deps) — re-running with a
    // fresh `search` would re-exchange an already-used authorization code,
    // which Xero rejects. `search` itself is a fresh object each render (from
    // the route's `validateSearch`), so including it would defeat the "once".
  }, []);

  return (
    <Paper withBorder radius="md" p="xl" maw={480}>
      <Stack gap="md" align="flex-start">
        <Title order={3}>Xero connection</Title>

        {status === 'loading' && (
          <Stack gap="xs" align="center" w="100%">
            <Loader size="sm" />
            <Text c="dimmed" size="sm">
              Completing the Xero connection…
            </Text>
          </Stack>
        )}

        {status === 'success' && (
          <Alert color="green" icon={<IconCircleCheck size={18} />} title="Connected" w="100%">
            Xero connection status: {result?.status}
            {result?.xeroTenantId ? ` (org ${result.xeroTenantId})` : ''}
          </Alert>
        )}

        {status === 'error' && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />} title="Could not complete connection" w="100%">
            {message}
          </Alert>
        )}

        <Button component={Link} to="/settings/xero" variant="light">
          Back to Xero settings
        </Button>
      </Stack>
    </Paper>
  );
}
